/**
 * Service for managing officials across multiple leagues with enhanced controls
 */

import { supabase } from '@/lib/supabaseClient';
import {
  LeagueOfficial,
  LeagueOfficialRelationshipType,
  LeagueOfficialStatus,
  OfficialModificationRequest,
  ModificationRequestType,
  ModificationRequestStatus,
  OfficialDuplicateCandidate,
  DuplicateOfficialSummary,
  DuplicateOfficialStats,
  DuplicateOfficialLeague,
  OfficialHistory,
  AddOfficialToLeagueRequest,
  AddOfficialToLeagueResponse,
  RequestOfficialModificationParams,
  ReviewModificationRequestParams,
  CheckDuplicateOfficialsParams,
  MergeOfficialsParams,
  OfficialWithLeagues,
  OfficialFilters,
  PaginatedOfficialsResponse,
  OfficialPlanningSnapshot,
  OfficialPlanningOptions,
  OfficialUpcomingAssignment,
  OfficialUpcomingUnavailability,
  OfficialActivityEntry,
  OfficialActivityOptions,
  OfficialCandidate,
  OfficialCandidateQuery,
} from '@/types/officials-scaling.types';
import { logAndThrow } from '@/utils/errorUtils';
import { applyLeagueMembership } from '@/lib/leagueScope/officialMembership';
import { checkEmailUniqueness, checkPhoneUniqueness, checkRibUniqueness } from './officialValidationService';

type RawOfficialRecord = Record<string, any>;

const parseString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Converts JavaScript camelCase field names to PostgreSQL snake_case column names
 * for the officials table
 */
function convertOfficialFieldsToDbColumns(fields: Record<string, any>): Record<string, any> {
  const columnMap: Record<string, string> = {
    firstName: 'first_name',
    lastName: 'last_name',
    firstNameAr: 'first_name_ar',
    lastNameAr: 'last_name_ar',
    bankRib: 'bank_account_number',
    bankAccountNumber: 'bank_account_number',
    locationId: 'location_id',
    isActive: 'is_active',
    createdAt: 'created_at',
    createdBy: 'created_by',
    updatedAt: 'updated_at',
    updatedBy: 'updated_by',
  };

  const converted: Record<string, any> = {};

  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined) continue;

    // Handle emails array specially
    if (key === 'emails' && Array.isArray(value)) {
      // Extract primary email (first item) for the email column
      if (value.length > 0) {
        converted['email'] = value[0];
      }
      // Note: Multiple emails are handled separately via official_emails table
      // Do NOT add 'emails' to the update payload as it's not a column in officials table
      continue;
    }

    // Convert field name using map, or keep as-is if not in map
    const dbColumn = columnMap[key] || key;
    converted[dbColumn] = value;
  }

  return converted;
}

function buildOfficialBase(record: RawOfficialRecord): Omit<OfficialWithLeagues, 'id' | 'leagues'> {
  const officialEmailsRaw: Array<{ email?: string; is_primary?: boolean }> = Array.isArray(record?.official_emails)
    ? record.official_emails
    : [];

  const normalizedEntries = officialEmailsRaw
    .map((entry) => {
      const email = parseString(entry?.email);
      if (!email) return null;
      return { email, isPrimary: Boolean(entry?.is_primary) };
    })
    .filter((entry): entry is { email: string; isPrimary: boolean } => Boolean(entry));

  normalizedEntries.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));

  const seen = new Set<string>();
  const orderedEmails: { email: string; isPrimary: boolean }[] = [];
  for (const entry of normalizedEntries) {
    const key = entry.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    orderedEmails.push(entry);
  }

  const columnEmail = parseString(record?.email);
  if (columnEmail) {
    const key = columnEmail.toLowerCase();
    if (seen.has(key)) {
      const existingIndex = orderedEmails.findIndex((entry) => entry.email.toLowerCase() === key);
      if (existingIndex > 0) {
        const [existing] = orderedEmails.splice(existingIndex, 1);
        orderedEmails.unshift({ ...existing, isPrimary: true });
      } else if (existingIndex === 0) {
        orderedEmails[0] = { ...orderedEmails[0], isPrimary: true };
      }
    } else {
      orderedEmails.unshift({ email: columnEmail, isPrimary: true });
      seen.add(key);
    }
  }

  if (orderedEmails.length === 0 && columnEmail) {
    orderedEmails.push({ email: columnEmail, isPrimary: true });
  }

  const primaryEmail = orderedEmails.find((entry) => entry.isPrimary)?.email
    ?? orderedEmails[0]?.email
    ?? columnEmail;

  const allEmails = orderedEmails.map((entry) => entry.email);
  if (allEmails.length === 0 && columnEmail) {
    allEmails.push(columnEmail);
  }

  const secondaryEmails = primaryEmail
    ? allEmails.filter((email) => email.toLowerCase() !== primaryEmail.toLowerCase())
    : allEmails.slice(1);

  const bankRaw = parseString(record?.bank_account_number) ?? parseString(record?.bank_rib);
  const address = parseString(record?.address);
  const phone = parseString(record?.phone);
  const category = parseString(record?.category) ?? 'referee';
  const createdByUserId = parseString(record?.created_by);
  const updatedByUserId = parseString(record?.updated_by);
  const createdAt = record?.created_at ? new Date(record.created_at) : undefined;
  const updatedAt = record?.updated_at ? new Date(record.updated_at) : undefined;

  return {
    firstName: record.first_name,
    lastName: record.last_name,
    firstNameAr: parseString(record?.first_name_ar),
    lastNameAr: parseString(record?.last_name_ar),
    email: primaryEmail ?? undefined,
    primaryEmail: primaryEmail ?? undefined,
    secondaryEmails: secondaryEmails.length > 0 ? secondaryEmails : undefined,
    emails: allEmails.length > 0 ? allEmails : undefined,
    phone: phone ?? undefined,
    category,
    bankRib: bankRaw ?? undefined,
    address: address ?? undefined,
    locationId: record?.location_id ?? undefined,
    isActive: Boolean(record?.is_active),
    createdAt,
    createdByUserId,
    updatedAt,
    updatedByUserId,
    duplicateCandidates: Array.isArray(record?.duplicateCandidates)
      ? record.duplicateCandidates
      : undefined,
    pendingRequests: Array.isArray(record?.pendingRequests)
      ? record.pendingRequests
      : undefined,
  };
}

// ============= Global Search =============

/**
 * Existing people resembling the name being typed in the add form (Latin and
 * Arabic compared part by part, tolerant to 1-2 letters, see
 * search_official_candidates). A failure returns [] : the form never blocks.
 */
export async function searchOfficialCandidates(query: OfficialCandidateQuery): Promise<OfficialCandidate[]> {
  const part = (v?: string | null) => (v ?? '').trim() || null;
  const lastName = part(query.lastName);
  const lastNameAr = part(query.lastNameAr);
  if ((lastName?.length ?? 0) < 2 && (lastNameAr?.length ?? 0) < 2) return [];

  const { data, error } = await supabase.rpc('search_official_candidates', {
    p_first_name: part(query.firstName),
    p_last_name: lastName,
    p_first_name_ar: part(query.firstNameAr),
    p_last_name_ar: lastNameAr,
    p_league_id: query.leagueId || null,
    p_location_id: query.locationId || null,
    p_category: query.category || null,
  });
  if (error) {
    console.error('Error searching official candidates:', error);
    return [];
  }
  return ((data as any[]) || []).map((r) => ({
    id: r.official_id,
    firstName: r.first_name ?? '',
    lastName: r.last_name ?? '',
    firstNameAr: r.first_name_ar ?? undefined,
    lastNameAr: r.last_name_ar ?? undefined,
    category: r.category ?? '',
    wilaya: r.wilaya ?? undefined,
    wilayaAr: r.wilaya_ar ?? undefined,
    sameWilaya: !!r.same_wilaya,
    sameCategory: !!r.same_category,
    inLeague: !!r.in_league,
    leagues: ((r.leagues as any[]) || []).map((l) => ({ leagueId: l.league_id, name: l.name, code: l.code })),
    score: Number(r.score),
  }));
}

// ============= League Officials Management =============

/**
 * Add an official to a league with duplicate detection
 */
export async function addOfficialToLeague(
  params: AddOfficialToLeagueRequest
): Promise<AddOfficialToLeagueResponse> {
  try {
    const { data, error } = await supabase.rpc('add_official_to_league', {
      p_league_id: params.leagueId,
      p_first_name: params.firstName,
      p_last_name: params.lastName,
      p_first_name_ar: params.firstNameAr || null,
      p_last_name_ar: params.lastNameAr || null,
      p_email: params.email || null,
      p_phone: params.phone || null,
      p_category: params.category || 'referee',
      p_position: params.position || null,
      p_location_id: params.locationId || null,
      p_address: params.address || null,
      p_bank_rib: params.bankRib || null,
      p_relationship_type: params.relationshipType || 'shared',
      // Only when confirmed: the 13-argument version (before Task 4) keeps resolving.
      ...(params.confirmNewPerson ? { p_confirm_new_person: true } : {}),
    });

    if (error) {
      logAndThrow(error, 'Failed to add official to league');
    }

    return data as AddOfficialToLeagueResponse;
  } catch (error) {
    console.error('Error adding official to league:', error);
    throw error;
  }
}

/**
 * Get all officials for a specific league with their relationships
 * Now supports pagination for better performance with large datasets
 */
export async function getLeagueOfficials(
  leagueId: string,
  filters?: OfficialFilters
): Promise<PaginatedOfficialsResponse> {
  try {
    // ==== RPC Fast Path for Ranked Search ====
    // If a search term is provided and the RPC function exists, attempt to use it.
    // The RPC should return ranked league_official rows (at least official_id & league_id + relationship metadata).
    // We then hydrate official details in a single batch query.
    if (filters?.searchTerm) {
      console.log('Using RPC path for ranked search with term:', filters.searchTerm);
      const pageSize = filters.pageSize || 50;
      try {
        const { data: rpcRows, error: rpcError } = await supabase.rpc('search_league_officials', {
          p_league: leagueId,
          p_query: filters.searchTerm,
        });
        console.log('RPC search results:', rpcRows);
        console.log('RPC search error:', rpcError, leagueId, filters.searchTerm);

        if (!rpcError && Array.isArray(rpcRows)) {
          // Collect IDs
          const officialIds = Array.from(new Set(rpcRows.map((r: any) => r.official_id).filter(Boolean)));

          // Hydrate official details
          // Use a loose any[] type to allow fallback without bank_rib
          // Hydrate with current canonical column name bank_account_number; bank_rib was deprecated.
          // We still map it to bankRib for backward compatibility in the UI layer.
          const { data: officialsData, error: officialsError } = await supabase
            .from('officials')
            .select('id, first_name, last_name, first_name_ar, last_name_ar, email, phone, category, bank_account_number, address, location_id, is_active, is_archived, created_at, created_by, updated_at, updated_by, official_emails:official_emails(email,is_primary), league_officials(league_id,category,position)')
            .in('id', officialIds)
            .or('is_archived.is.null,is_archived.eq.false');

          if (officialsError) {
            console.warn('Hydration error after search_league_officials RPC – falling back to legacy query:', officialsError.message);
          } else {
            const officialsMap = new Map<string, any>();
            for (const o of officialsData || []) {
              officialsMap.set(o.id, o);
            }

            // Transform combined rows
            const combined: OfficialWithLeagues[] = [];
            const byId = new Map<string, OfficialWithLeagues>();
            for (const rel of rpcRows) {
              const o = officialsMap.get(rel.official_id);
              if (!o) continue; // skip if hydration failed for this id

              if (!byId.has(rel.official_id)) {
                // Grade of THIS league (membership), person's grade as fallback.
                const base = buildOfficialBase(applyLeagueMembership(o, leagueId));
                byId.set(rel.official_id, {
                  id: rel.official_id,
                  ...base,
                  leagues: [],
                });
                combined.push(byId.get(rel.official_id)!);
              }

              const entry = byId.get(rel.official_id)!;
              entry.leagues.push({
                leagueId: rel.league_id,
                leagueName: rel.league_name || undefined, // if RPC added league_name
                relationshipType: rel.relationship_type as LeagueOfficialRelationshipType,
                status: rel.status as LeagueOfficialStatus,
                canEdit: rel.can_be_edited_by_league,
                addedAt: rel.added_at ? new Date(rel.added_at) : new Date(),
              });
            }

            // Apply additional client-side filters (category, status, relationshipType) if present
            let filtered = combined;
            if (filters.category) filtered = filtered.filter(o => o.category === filters.category);
            if (filters.status) filtered = filtered.filter(o => o.leagues.some(l => l.status === filters.status));
            if (filters.relationshipType) filtered = filtered.filter(o => o.leagues.some(l => l.relationshipType === filters.relationshipType));

            return {
              data: filtered,
              total: filtered.length, // RPC currently returns a limited ranked set
              page: 1,
              pageSize,
              totalPages: 1,
            };
          }
        } else if (rpcError) {
          console.warn('search_league_officials RPC error – fallback to legacy query:', rpcError.message);
        }
      } catch (rpcCatch) {
        console.warn('search_league_officials RPC threw – fallback to legacy query:', (rpcCatch as Error).message);
      }
      // If RPC path failed, continue to legacy path below.
    }

    // Build base query for both count and data
    const buildQuery = () => {
      let query = supabase
        .from('league_officials')
        .select(`
          *,
          officials!inner (
            id,
            first_name,
            last_name,
            first_name_ar,
            last_name_ar,
            email,
            phone,
            category,
            address,
            location_id,
            bank_account_number,
            is_active,
            is_archived,
            created_at,
            created_by,
            updated_at,
            updated_by,
            official_emails:official_emails(email,is_primary)
          ),
          leagues!inner (
            id,
            name
          )
  `, { count: 'exact' })
        .eq('league_id', leagueId)
        .eq('officials.is_active', true)
        .eq('officials.is_archived', false); // Only show active, non-archived officials

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.relationshipType) {
        query = query.eq('relationship_type', filters.relationshipType);
      }
      if (filters?.category) {
        // Grade is per league (league_officials.category, filled from the person on insert).
        query = query.eq('category', filters.category);
      }
      if (filters?.searchTerm) {
        const searchPattern = `%${filters.searchTerm}%`;
        query = query.or(
          `first_name.ilike.${searchPattern},` +
          `last_name.ilike.${searchPattern},` +
          `first_name_ar.ilike.${searchPattern},` +
          `last_name_ar.ilike.${searchPattern},` +
          `email.ilike.${searchPattern},` +
          `phone.ilike.${searchPattern},` +
          `bank_account_number.ilike.${searchPattern}`,
          { foreignTable: 'officials' }
        );
      }

      // Advanced Filters (Server-side)
      if (filters?.hasEmail === false) {
        query = query.filter('officials.email', 'is', null);
      } else if (filters?.hasEmail === true) {
        query = query.filter('officials.email', 'not.is', null);
      }

      if (filters?.hasPhone === false) {
        query = query.filter('officials.phone', 'is', null);
      } else if (filters?.hasPhone === true) {
        query = query.filter('officials.phone', 'not.is', null);
      }

      if (filters?.missingRib === true) {
        query = query.or('bank_account_number.is.null,bank_account_number.eq.""', { foreignTable: 'officials' });
      }

      if (filters?.validRib === true) {
        query = query.filter('officials.bank_account_number', 'not.is', null)
          .filter('officials.bank_account_number', 'match', '^[0-9]{20}$');
      }

      if (filters?.invalidRib === true) {
        query = query.filter('officials.bank_account_number', 'not.is', null)
          .filter('officials.bank_account_number', 'neq', '')
          .filter('officials.bank_account_number', 'not.match', '^[0-9]{20}$');
      }

      if (filters?.incompleteProfile === true) {
        query = query.or(
          'email.is.null,' +
          'phone.is.null,' +
          'bank_account_number.is.null,' +
          'bank_account_number.eq.""',
          { foreignTable: 'officials' }
        );
      }

      return query;
    };

    // Get page and pageSize from filters or use defaults
    const page = filters?.page || 1;
    const pageSize = filters?.pageSize || 50;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Execute query with pagination
    let query = buildQuery();
    query = query.range(from, to);

    let { data, error, count } = await query;

    if (error && error.code === '42703') {
      const missingMessage = error.message || '';
      console.warn('league_officials column missing – retrying with compatibility selection', {
        message: missingMessage,
      });

      const missingBankAccount = /bank_account_number/.test(missingMessage);
      const missingEmailsRelation = /official_emails/.test(missingMessage);

      const runCompatibilityQuery = async (
        options: { bankColumn?: 'bank_account_number' | 'bank_rib'; includeEmails: boolean }
      ) => {
        const officialFields = [
          'id',
          'first_name',
          'last_name',
          'first_name_ar',
          'last_name_ar',
          'email',
          'phone',
          'category',
          'address',
          'location_id',
          'created_at',
          'created_by',
          'updated_at',
          'updated_by',
        ];
        if (options.bankColumn) {
          officialFields.push(options.bankColumn);
        }
        officialFields.push('is_active', 'is_archived');
        if (options.includeEmails) {
          officialFields.push('official_emails:official_emails(email,is_primary)');
        }

        const selectFragment = `
            *,
            officials!inner (
              ${officialFields.join(',\n              ')}
            ),
            leagues!inner (
              id,
              name
            )
          `;

        let q = supabase
          .from('league_officials')
          .select(selectFragment, { count: 'exact' })
          .eq('league_id', leagueId)
          .eq('officials.is_active', true)
          .eq('officials.is_archived', false);

        if (filters?.status) q = q.eq('status', filters.status);
        if (filters?.relationshipType) q = q.eq('relationship_type', filters.relationshipType);
        if (filters?.category) q = q.eq('officials.category', filters.category);
        if (filters?.searchTerm) {
          const searchPattern = `%${filters.searchTerm}%`;
          q = q.or(
            `first_name.ilike.${searchPattern},` +
            `last_name.ilike.${searchPattern},` +
            `first_name_ar.ilike.${searchPattern},` +
            `last_name_ar.ilike.${searchPattern},` +
            `email.ilike.${searchPattern},` +
            `phone.ilike.${searchPattern},` +
            `bank_account_number.ilike.${searchPattern}`,
            { foreignTable: 'officials' }
          );
        }

        if (filters?.hasEmail === false) {
          q = q.filter('officials.email', 'is', null);
        } else if (filters?.hasEmail === true) {
          q = q.filter('officials.email', 'not.is', null);
        }

        if (filters?.hasPhone === false) {
          q = q.filter('officials.phone', 'is', null);
        } else if (filters?.hasPhone === true) {
          q = q.filter('officials.phone', 'not.is', null);
        }

        if (filters?.missingRib === true) {
          q = q.or('bank_account_number.is.null,bank_account_number.eq.""', { foreignTable: 'officials' });
        }

        if (filters?.validRib === true) {
          q = q.filter('officials.bank_account_number', 'not.is', null)
            .filter('officials.bank_account_number', 'match', '^[0-9]{20}$');
        }

        if (filters?.invalidRib === true) {
          q = q.filter('officials.bank_account_number', 'not.is', null)
            .filter('officials.bank_account_number', 'neq', '')
            .filter('officials.bank_account_number', 'not.match', '^[0-9]{20}$');
        }

        if (filters?.incompleteProfile === true) {
          q = q.or(
            'email.is.null,' +
            'phone.is.null,' +
            'bank_account_number.is.null,' +
            'bank_account_number.eq.""',
            { foreignTable: 'officials' }
          );
        }

        return q.range(from, to);
      };

      let retry = await runCompatibilityQuery({
        bankColumn: missingBankAccount ? 'bank_rib' : 'bank_account_number',
        includeEmails: !missingEmailsRelation,
      });

      if (retry.error && retry.error.code === '42703' && missingBankAccount) {
        retry = await runCompatibilityQuery({
          bankColumn: undefined,
          includeEmails: !missingEmailsRelation,
        });
      }

      if (retry.error && retry.error.code === '42703' && missingEmailsRelation) {
        retry = await runCompatibilityQuery({
          bankColumn: missingBankAccount ? undefined : 'bank_account_number',
          includeEmails: false,
        });
      }

      data = retry.data;
      count = retry.count;
      error = retry.error;
    }

    if (error) {
      logAndThrow(error, 'Failed to get league officials');
    }

    // Transform the data
    const officialsMap = new Map<string, OfficialWithLeagues>();

    for (const item of data || []) {
      if (item?.officials?.is_archived === true) {
        continue;
      }
      const officialId = item.officials.id;

      if (!officialsMap.has(officialId)) {
        // `*` brings the membership's category / position (when the columns exist):
        // the grade shown is the one of this league, the person's as fallback.
        const base = buildOfficialBase(
          applyLeagueMembership({ ...item.officials, league_officials: item }, leagueId)
        );
        officialsMap.set(officialId, {
          id: officialId,
          ...base,
          leagues: [],
        });
      }

      const official = officialsMap.get(officialId)!;
      official.leagues.push({
        leagueId: item.league_id,
        leagueName: item.leagues.name,
        relationshipType: item.relationship_type as LeagueOfficialRelationshipType,
        status: item.status as LeagueOfficialStatus,
        canEdit: item.can_be_edited_by_league,
        addedAt: new Date(item.added_at),
      });
    }

    const officials = Array.from(officialsMap.values());
    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    return {
      data: officials,
      total,
      page,
      pageSize,
      totalPages,
    };
  } catch (error) {
    console.error('Error getting league officials:', error);
    throw error;
  }
}

/**
 * Update the relationship between an official and a league
 */
export async function updateLeagueOfficialRelationship(
  leagueId: string,
  officialId: string,
  updates: Partial<LeagueOfficial>
): Promise<void> {
  try {
    // Map camelCase fields from the TypeScript model to snake_case DB columns
    const columnMap: Record<string, string> = {
      relationshipType: 'relationship_type',
      canBeEditedByLeague: 'can_be_edited_by_league',
      approvedByAdmin: 'approved_by_admin',
      deactivatedAt: 'deactivated_at',
      deactivationReason: 'deactivation_reason',
      validFromSeason: 'valid_from_season',
      validToSeason: 'valid_to_season',
      addedBy: 'added_by',
      addedAt: 'added_at',
      status: 'status',
      notes: 'notes',
    };

    const transformed: Record<string, any> = {};
    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined) continue;
      const snake = columnMap[key];
      if (snake) transformed[snake] = value instanceof Date ? value.toISOString() : value;
      else {
        // Allow already snake_case or unexpected keys to pass through if they look snake_case
        if (/^[a-z0-9_]+$/.test(key)) transformed[key] = value instanceof Date ? value.toISOString() : value;
      }
    }

    let { data, error } = await supabase
      .from('league_officials')
      .update(transformed)
      .eq('league_id', leagueId)
      .eq('official_id', officialId)
      .select('id');

    // Retry with snake_case mapping if initial attempt failed due to schema cache missing camelCase columns
    if (error && (error.code === 'PGRST204' || error.code === '42703')) {
      console.warn('Retrying updateLeagueOfficialRelationship with transformed snake_case payload due to error:', {
        code: error.code,
        message: error.message,
      });
      // If we somehow tried original (camelCase) first (should not now), reattempt with mapping only
      const retryPayload: Record<string, any> = {};
      for (const [camel, snake] of Object.entries(columnMap)) {
        if ((updates as any)[camel] !== undefined) {
          const v = (updates as any)[camel];
          retryPayload[snake] = v instanceof Date ? v.toISOString() : v;
        }
      }
      const retryResp = await supabase
        .from('league_officials')
        .update(retryPayload)
        .eq('league_id', leagueId)
        .eq('official_id', officialId)
        .select('id');
      error = retryResp.error;
      data = retryResp.data;
    }

    if (!error && (!data || data.length === 0)) {
      throw new Error('Aucune relation ligue-officiel correspondante à mettre à jour');
    }

    if (error) {
      logAndThrow(error, 'Failed to update league-official relationship');
    }
  } catch (error) {
    console.error('Error updating league-official relationship:', error);
    throw error;
  }
}

// Partition des officiels partagés : services/sharedOfficialsService.ts.
export { bulkUpdateOfficialsLocation } from './sharedOfficialsService';

// ============= Modification Requests =============

/**
 * Create a modification request for an official
 */
export async function requestOfficialModification(
  params: RequestOfficialModificationParams
): Promise<OfficialModificationRequest> {
  try {
    // First, get the current values
    const { data: official, error: officialError } = await supabase
      .from('officials')
      .select('*')
      .eq('id', params.officialId)
      .single();

    if (officialError) {
      logAndThrow(officialError, 'Failed to get official data');
    }

    // Create snapshot of current values for the fields being changed
    const currentValues: Record<string, any> = {};
    for (const key of Object.keys(params.proposedChanges)) {
      if (key in official) {
        currentValues[key] = official[key];
      }
    }

    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();

    // Create the modification request
    const { data, error } = await supabase
      .from('official_modification_requests')
      .insert({
        official_id: params.officialId,
        requested_by_league_id: params.leagueId,
        requested_by_user_id: user?.id,
        request_type: params.requestType,
        proposed_changes: params.proposedChanges,
        current_values: currentValues,
      })
      .select()
      .single();

    if (error) {
      logAndThrow(error, 'Failed to create modification request');
    }

    return data as OfficialModificationRequest;
  } catch (error) {
    console.error('Error requesting official modification:', error);
    throw error;
  }
}

/**
 * Directly update official data (for creators only)
 * Bypasses the modification request workflow
 */
export async function updateOfficialDirect(
  officialId: string,
  updates: Record<string, any>
): Promise<void> {
  try {
    // Convert camelCase field names to snake_case column names
    const dbUpdates = convertOfficialFieldsToDbColumns(updates);

    const { error } = await supabase
      .from('officials')
      .update(dbUpdates)
      .eq('id', officialId);

    if (error) {
      logAndThrow(error, 'Failed to update official');
    }
  } catch (error) {
    console.error('Error updating official:', error);
    throw error;
  }
}

/**
 * Get pending modification requests for a league
 */
export async function getPendingModificationRequests(
  leagueId?: string
): Promise<OfficialModificationRequest[]> {
  try {
    // Attempt joined query (profiles is the proper FK target), fallback to hydration if relationship not cached yet
    let base = supabase
      .from('official_modification_requests')
      .select(`
        *,
        officials (
          id,
          first_name,
          last_name,
          first_name_ar,
          last_name_ar,
          category
        ),
        leagues (
          id,
          name
        ),
        profiles!requested_by_user_id ( id, email )
      `)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    if (leagueId) base = base.eq('requested_by_league_id', leagueId);

    let { data, error } = await base;
    if (error && (error.code === 'PGRST200' || (error.code === '42703' && /(display_name|full_name)/.test(error.message || '')))) {
      // Fallback: fetch without profiles join then hydrate user info via profiles table
      console.warn('profiles join/optional columns unavailable – falling back to manual hydration for modification requests', {
        code: error.code,
        message: error.message,
      });
      let plain = supabase
        .from('official_modification_requests')
        .select(`*, officials ( id, first_name, last_name, first_name_ar, last_name_ar, category ), leagues ( id, name )`)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (leagueId) plain = plain.eq('requested_by_league_id', leagueId);
      const plainResp = await plain;
      data = plainResp.data;
      error = plainResp.error;
      if (!error && data) {
        const userIds = Array.from(new Set((data as any[]).map(r => r.requested_by_user_id).filter(Boolean)));
        if (userIds.length) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            // Try to select display_name; if missing will still hydrate email/id
            .select('id, email, display_name, full_name')
            .in('id', userIds);
          const profileMap = new Map<string, any>();
          if (!profilesError) {
            for (const p of profiles || []) profileMap.set(p.id, p);
            data = (data as any[]).map(row => ({
              ...row,
              profiles: profileMap.get(row.requested_by_user_id) || null,
              users: profileMap.get(row.requested_by_user_id) || null, // backward compatibility alias
            }));
          }
        }
      }
    }
    // If joined query succeeded but UI still expects users field, alias profiles to users
    if (!error && data) {
      data = (data as any[]).map(row => {
        if (row.profiles && !row.users) {
          return { ...row, users: row.profiles };
        }
        return row;
      });
    }
    if (error) {
      console.error('getPendingModificationRequests terminal error', {
        code: (error as any).code,
        message: (error as any).message,
        details: (error as any).details,
        hint: (error as any).hint,
      });
      logAndThrow(error, 'Failed to get modification requests');
    }
    return (data || []) as OfficialModificationRequest[];
  } catch (error) {
    console.error('Error getting modification requests:', error);
    throw error;
  }
}

/**
 * Review (approve or reject) a modification request
 */
export async function reviewModificationRequest(
  params: ReviewModificationRequestParams
): Promise<void> {
  try {
    const status: ModificationRequestStatus =
      params.action === 'approve' ? 'approved' : 'rejected';

    // Read the request (no transaction: the change is applied before the status, see below)
    const { data: request, error: requestError } = await supabase
      .from('official_modification_requests')
      .select('*')
      .eq('id', params.requestId)
      .single();

    if (requestError) {
      logAndThrow(requestError, 'Failed to get modification request');
    }

    // If approved, apply the changes FIRST: the person-write guard can refuse them
    // (officiel devenu partagé après la demande → OFFICIAL_SHARED_REQUIRES_SUPER_ADMIN /
    // OFFICIAL_PERSON_OUT_OF_SCOPE). The request is marked approved only once applied,
    // so a refusal leaves it pending and the caller shows the translated error.
    if (params.action === 'approve' && request) {
      // Validate uniqueness constraints before applying changes
      const validationErrors: string[] = [];

      // Check email uniqueness if being changed
      if (request.proposed_changes.emails && Array.isArray(request.proposed_changes.emails)) {
        const primaryEmail = request.proposed_changes.emails[0];
        if (primaryEmail) {
          const emailValidation = await checkEmailUniqueness(
            primaryEmail,
            request.official_id,
            null // Check globally, not just in one league
          );

          if (!emailValidation.is_unique) {
            const conflictInfo = emailValidation.conflict
              ? ` (déjà utilisé par ${emailValidation.conflict.official_name})`
              : '';
            validationErrors.push(`L'email ${primaryEmail} existe déjà${conflictInfo}`);
          }
        }
      }

      // Check phone uniqueness if being changed
      if (request.proposed_changes.phone) {
        const phoneValidation = await checkPhoneUniqueness(
          request.proposed_changes.phone,
          request.official_id,
          null
        );

        if (!phoneValidation.is_unique) {
          const conflictInfo = phoneValidation.conflict
            ? ` (déjà utilisé par ${phoneValidation.conflict.official_name})`
            : '';
          validationErrors.push(`Le téléphone existe déjà${conflictInfo}`);
        }
      }

      // Check RIB uniqueness if being changed
      if (request.proposed_changes.bankRib || request.proposed_changes.bankAccountNumber) {
        const rib = request.proposed_changes.bankRib || request.proposed_changes.bankAccountNumber;
        const ribValidation = await checkRibUniqueness(
          rib,
          request.official_id,
          null
        );

        if (!ribValidation.is_unique) {
          const conflictInfo = ribValidation.conflict
            ? ` (déjà utilisé par ${ribValidation.conflict.official_name})`
            : '';
          validationErrors.push(`Le RIB existe déjà${conflictInfo}`);
        }
      }

      // If there are validation errors, throw them
      if (validationErrors.length > 0) {
        throw new Error(
          `Impossible d'approuver cette demande - conflit(s) détecté(s):\n${validationErrors.join('\n')}`
        );
      }

      // Convert camelCase field names to snake_case column names
      const dbUpdates = convertOfficialFieldsToDbColumns(request.proposed_changes);

      const { data: updatedOfficials, error: officialError } = await supabase
        .from('officials')
        .update(dbUpdates)
        .eq('id', request.official_id)
        .select('id');

      if (officialError) {
        // Provide more specific error message for constraint violations
        if (officialError.code === '23505' || officialError.message?.includes('duplicate')) {
          throw new Error('Une contrainte d\'unicité a été violée. Veuillez vérifier que l\'email, le téléphone et le RIB ne sont pas déjà utilisés.');
        }
        logAndThrow(officialError, 'Failed to update official');
      }

      // No row back = RLS let nothing through: never mark the request approved.
      if (!updatedOfficials || updatedOfficials.length === 0) {
        throw new Error("La modification n'a pas pu être appliquée à l'officiel (droits insuffisants).");
      }

      // Handle emails separately if they were changed
      if (request.proposed_changes.emails && Array.isArray(request.proposed_changes.emails)) {
        const emails = request.proposed_changes.emails;

        // Delete existing email records
        const { error: deleteError } = await supabase
          .from('official_emails')
          .delete()
          .eq('official_id', request.official_id);

        if (deleteError) {
          console.error('Failed to delete old emails:', deleteError);
          // Continue anyway - the primary email in officials table was updated
        }

        // Insert new email records
        if (emails.length > 0) {
          const emailRecords = emails.map((email: string, index: number) => ({
            official_id: request.official_id,
            email: email,
            is_primary: index === 0, // First email is primary
          }));

          const { error: insertError } = await supabase
            .from('official_emails')
            .insert(emailRecords);

          if (insertError) {
            console.error('Failed to insert new emails:', insertError);
            // Continue anyway - the primary email in officials table was updated
          }
        }
      }
    }

    // Update the request status and return the updated row to detect RLS denials
    const { data: updatedRequest, error: updateError } = await supabase
      .from('official_modification_requests')
      .update({
        status,
        reviewed_by: (await supabase.auth.getUser()).data.user?.id,
        reviewed_at: new Date().toISOString(),
        review_notes: params.notes,
      })
      .eq('id', params.requestId)
      .select('*')
      .single();

    if (updateError) {
      logAndThrow(updateError, 'Failed to update modification request');
    }

    if (!updatedRequest) {
      throw new Error('Modification request could not be updated. Check permissions.');
    }
  } catch (error) {
    console.error('Error reviewing modification request:', error);
    throw error;
  }
}

// ============= Duplicate Detection =============

const safeDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value as string);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const mapDuplicateLeagues = (entries: any[]): DuplicateOfficialLeague[] => {
  if (!Array.isArray(entries)) return [];

  return entries
    .map((entry) => {
      if (!entry) return undefined;
      const leagueId = entry.league_id;
      const status = entry.status;
      const relationshipType = entry.relationship_type;
      if (!leagueId || !status || !relationshipType) return undefined;

      const leagueName = parseString(entry?.leagues?.name) || 'Inconnu';

      return {
        leagueId,
        leagueName,
        status,
        relationshipType,
        addedAt: safeDate(entry.added_at),
      } as DuplicateOfficialLeague;
    })
    .filter((value): value is DuplicateOfficialLeague => Boolean(value));
};

function buildDuplicateOfficialSummary(
  raw: any,
  stats?: DuplicateOfficialStats
): DuplicateOfficialSummary | undefined {
  if (!raw) return undefined;

  const leagues = mapDuplicateLeagues(raw.league_officials);

  return {
    id: raw.id,
    firstName: raw.first_name,
    lastName: raw.last_name,
    firstNameAr: parseString(raw.first_name_ar),
    lastNameAr: parseString(raw.last_name_ar),
    email: parseString(raw.email),
    phone: parseString(raw.phone),
    category: parseString(raw.category),
    isActive: typeof raw.is_active === 'boolean' ? raw.is_active : undefined,
    leagues,
    leagueCount: leagues.length,
    stats,
  };
}

async function fetchDuplicateStats(
  officialIds: string[]
): Promise<Map<string, DuplicateOfficialStats>> {
  const statsMap = new Map<string, DuplicateOfficialStats>();
  const uniqueIds = Array.from(new Set(officialIds.filter(Boolean)));

  if (uniqueIds.length === 0) {
    return statsMap;
  }

  await Promise.all(
    uniqueIds.map(async (officialId) => {
      try {
        const { data, error } = await supabase.rpc('get_official_profile_and_stats', {
          p_user_id: null,
          p_official_id: officialId,
        });

        if (error) {
          console.warn('Failed to fetch stats for official', officialId, error);
          return;
        }

        const statsPayload = (data?.stats as Record<string, any>) || {};

        statsMap.set(officialId, {
          totalAssignments: Number(statsPayload.totalAssignments) || 0,
          acceptedAssignments: Number(statsPayload.acceptedAssignments) || 0,
          upcomingAssignments: Number(statsPayload.upcomingAssignments) || 0,
          completedAssignments: Number(statsPayload.completedAssignments) || 0,
          acceptanceRate: Number(statsPayload.acceptanceRate) || 0,
        });
      } catch (err) {
        console.warn('Error fetching stats for official', officialId, err);
      }
    })
  );

  return statsMap;
}

/**
 * Check for duplicate officials
 */
export async function checkDuplicateOfficials(
  params: CheckDuplicateOfficialsParams
): Promise<OfficialDuplicateCandidate[]> {
  try {
    // Build the query to find similar officials
    let orConditions = [];
    if (params.email) {
      orConditions.push(`email.ilike.${params.email.trim()}`);
    }
    if (params.phone) {
      // Strip formatting from phone for better matching
      const cleanPhone = params.phone.replace(/[^0-9]/g, '');
      if (cleanPhone.length > 0) {
        orConditions.push(`phone.ilike.%${cleanPhone}%`);
      }
    }

    // Helper to get first word for broader matching
    const getFirstWord = (s: string) => s.trim().split(/\s+/)[0];

    // Check Latin names
    // FIX 1: Added wildcards (%) to last_name check to handle DB padding ("CHAOUBI   ")
    if (params.firstName && params.lastName) {
      const fnFirst = getFirstWord(params.firstName);
      const lnFirst = getFirstWord(params.lastName);

      orConditions.push(
        `and(last_name.ilike.${params.lastName.trim()}%,first_name.ilike.${fnFirst}%)`
      );
      // Handle reversed names
      orConditions.push(
        `and(first_name.ilike.${params.lastName.trim()}%,last_name.ilike.${lnFirst}%)`
      );
    }

    // Check Arabic names
    // FIX 2: Added wildcards for Arabic names as well
    if (params.firstNameAr && params.lastNameAr) {
      const fnFirstAr = getFirstWord(params.firstNameAr);
      const lnFirstAr = getFirstWord(params.lastNameAr);

      orConditions.push(
        `and(last_name_ar.ilike.${params.lastNameAr.trim()}%,first_name_ar.ilike.${fnFirstAr}%)`
      );
      orConditions.push(
        `and(first_name_ar.ilike.${params.firstNameAr.trim()}%,last_name_ar.ilike.${lnFirstAr}%)`
      );
    }

    // If we have no conditions, return empty
    if (orConditions.length === 0) return [];

    const { data: similars, error } = await supabase
      .from('officials')
      .select('*')
      .or(orConditions.join(','))
      .neq('is_archived', true); // Optionally exclude archived

    if (error) {
      logAndThrow(error, 'Failed to check for duplicates');
    }

    // Calculate similarity scores
    const duplicates: OfficialDuplicateCandidate[] = [];

    // Normalize params for smart checking
    const paramNameNorm = `${params.firstName || ''} ${params.lastName || ''}`.toLowerCase().replace(/\s+/g, '');

    for (const similar of similars || []) {
      // 1. Check Latin name similarity
      const nameSimilarity = calculateNameSimilarity(
        `${params.firstName} ${params.lastName}`,
        `${similar.first_name} ${similar.last_name}`
      );

      // 2. Check Arabic name similarity
      let arNameSimilarity = 0;
      if (params.firstNameAr && params.lastNameAr && similar.first_name_ar && similar.last_name_ar) {
        arNameSimilarity = calculateNameSimilarity(
          `${params.firstNameAr} ${params.lastNameAr}`,
          `${similar.first_name_ar} ${similar.last_name_ar}`
        );
      }

      // 3. Smart Email Check (New): Does the candidate's email contain the param's name?
      // Useful when param official has no email, but candidate official has an email like "chaoubi.mohamed..."
      let smartEmailMatch = false;
      if (similar.email && params.lastName && params.firstName) {
        const emailUser = similar.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
        const pLast = params.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
        const pFirst = params.firstName.toLowerCase().replace(/[^a-z0-9]/g, '');

        // Check if email contains both first and last name parts
        if (emailUser.includes(pLast) && emailUser.includes(pFirst)) {
          smartEmailMatch = true;
        }
      }

      const maxSimilarity = Math.max(nameSimilarity, arNameSimilarity);

      // FIX 3: Lower threshold slightly or include Smart Email Match as a trigger
      if (
        maxSimilarity > 0.7 ||
        smartEmailMatch ||
        (params.email && similar.email && similar.email.toLowerCase() === params.email.toLowerCase()) ||
        (params.phone && similar.phone && similar.phone.replace(/[^0-9]/g, '') === params.phone.replace(/[^0-9]/g, ''))
      ) {
        duplicates.push({
          id: crypto.randomUUID(),
          official1Id: '',
          official2Id: similar.id,
          nameSimilarity: maxSimilarity,
          emailMatch: (params.email && similar.email && similar.email === params.email) || smartEmailMatch,
          phoneMatch: params.phone && similar.phone && similar.phone === params.phone,
          bankAccountMatch: false, // We can check this if params has bank info
          status: 'pending',
          detectedAt: new Date(),
          detectionMethod: smartEmailMatch ? 'smart_email_inference' : 'automatic',
        } as OfficialDuplicateCandidate);
      }
    }

    return duplicates;
  } catch (error) {
    console.error('Error checking for duplicates:', error);
    throw error;
  }
}

/**
 * Get unresolved duplicate candidates.
 *
 * With `leagueId`, keeps only the pairs where at least one of the two officials
 * belongs to that league (`league_officials`): a super-admin switching league
 * sees that league's pairs, and the badge counts the same list. RLS already
 * hides pairs outside the caller's leagues.
 */
export async function getUnresolvedDuplicates(leagueId?: string): Promise<OfficialDuplicateCandidate[]> {
  try {
    const { data, error } = await supabase
      .from('official_duplicate_candidates')
      .select(`
        id,
        official1_id,
        official2_id,
        name_similarity,
        email_match,
        phone_match,
        bank_account_match,
        status,
        merged_into_id,
        resolved_by,
        resolved_at,
        resolution_notes,
        detected_at,
        detection_method,
        official1:officials!official1_id (
          id,
          first_name,
          last_name,
          first_name_ar,
          last_name_ar,
          email,
          phone,
          category,
          is_active,
          league_officials (
            league_id,
            status,
            relationship_type,
            added_at,
            leagues (
              id,
              name
            )
          )
        ),
        official2:officials!official2_id (
          id,
          first_name,
          last_name,
          first_name_ar,
          last_name_ar,
          email,
          phone,
          category,
          is_active,
          league_officials (
            league_id,
            status,
            relationship_type,
            added_at,
            leagues (
              id,
              name
            )
          )
        )
      `)
      .eq('status', 'pending')
      .order('detected_at', { ascending: false })
      .limit(500);

    if (error) {
      logAndThrow(error, 'Failed to get duplicate candidates');
    }

    const belongsToLeague = (official: any) =>
      Array.isArray(official?.league_officials)
      && official.league_officials.some((lo: any) => lo?.league_id === leagueId);
    const rows = (data || []).filter((row: any) =>
      !leagueId || belongsToLeague(row?.official1) || belongsToLeague(row?.official2));
    const ids: string[] = [];

    rows.forEach((row: any) => {
      if (row?.official1?.id) ids.push(row.official1.id);
      if (row?.official2?.id) ids.push(row.official2.id);
    });

    const statsMap = await fetchDuplicateStats(ids);

    return rows.map((row: any) => {
      const official1Stats = row?.official1?.id ? statsMap.get(row.official1.id) : undefined;
      const official2Stats = row?.official2?.id ? statsMap.get(row.official2.id) : undefined;

      return {
        id: row.id,
        official1Id: row.official1_id,
        official2Id: row.official2_id,
        nameSimilarity: typeof row.name_similarity === 'number' ? row.name_similarity : undefined,
        emailMatch: Boolean(row.email_match),
        phoneMatch: Boolean(row.phone_match),
        bankAccountMatch: Boolean(row.bank_account_match),
        status: row.status,
        mergedIntoId: row.merged_into_id || undefined,
        resolvedBy: row.resolved_by || undefined,
        resolvedAt: safeDate(row.resolved_at),
        resolutionNotes: row.resolution_notes || undefined,
        detectedAt: safeDate(row.detected_at) || new Date(),
        detectionMethod: row.detection_method || undefined,
        official1: buildDuplicateOfficialSummary(row.official1, official1Stats),
        official2: buildDuplicateOfficialSummary(row.official2, official2Stats),
      } as OfficialDuplicateCandidate;
    });
  } catch (error) {
    console.error('Error getting duplicate candidates:', error);
    throw error;
  }
}

export async function rescanDuplicateCandidates(limit?: number): Promise<{ inserted: number }> {
  try {
    const { data, error } = await supabase.rpc('rescan_official_duplicate_candidates', {
      p_limit: limit ?? null,
    });

    if (error) {
      logAndThrow(error, 'Failed to rescan duplicate candidates');
    }

    const inserted = typeof data === 'number'
      ? data
      : Number((data as any)?.inserted ?? 0);

    return { inserted: Number.isNaN(inserted) ? 0 : inserted };
  } catch (error) {
    console.error('Error rescanning duplicate candidates:', error);
    throw error;
  }
}

/**
 * Resolve a duplicate candidate
 */
export async function resolveDuplicate(
  duplicateId: string,
  resolution: 'confirmed_duplicate' | 'not_duplicate' | 'merge',
  mergeIntoId?: string,
  notes?: string
): Promise<void> {
  try {
    const updates: any = {
      status: resolution === 'merge' ? 'merged' : resolution,
      resolved_by: (await supabase.auth.getUser()).data.user?.id,
      resolved_at: new Date().toISOString(),
      resolution_notes: notes,
    };

    if (mergeIntoId) {
      updates.merged_into_id = mergeIntoId;
    }

    const { error } = await supabase
      .from('official_duplicate_candidates')
      .update(updates)
      .eq('id', duplicateId);

    if (error) {
      logAndThrow(error, 'Failed to resolve duplicate');
    }
  } catch (error) {
    console.error('Error resolving duplicate:', error);
    throw error;
  }
}

/**
 * Merge two officials
 */
export async function mergeOfficials(params: MergeOfficialsParams): Promise<void> {
  try {
    const { relatedLeagueIds, ...remaining } = params;
    // This would be a complex operation that:
    // 1. Transfers all assignments from source to target
    // 2. Transfers all league relationships
    // 3. Merges contact information based on strategy
    // 4. Deactivates the source official
    // 5. Records the merge in history

    // For now, we'll use a database function
    const { error } = await supabase.rpc('merge_officials', {
      p_source_id: remaining.sourceOfficialId,
      p_target_id: remaining.targetOfficialId,
      p_merge_strategy: remaining.mergeStrategy,
      p_field_overrides: remaining.fieldOverrides || {},
    });

    if (error) {
      logAndThrow(error, 'Failed to merge officials');
    }
  } catch (error) {
    console.error('Error merging officials:', error);
    throw error;
  }
}

// ============= History Tracking =============

/**
 * Get history for an official
 */
export async function getOfficialHistory(
  officialId: string,
  limit: number = 50
): Promise<OfficialHistory[]> {
  try {
    const { data, error } = await supabase
      .from('official_history')
      .select(`
        *,
        profiles!changed_by_user_id (
          id,
          email,
          full_name
        ),
        leagues!changed_by_league_id (
          id,
          name
        )
      `)
      .eq('official_id', officialId)
      .order('changed_at', { ascending: false })
      .limit(limit);

    if (error) {
      logAndThrow(error, 'Failed to get official history');
    }

    // Alias profiles to users for backward compatibility in consumers
    const history = (data || []).map((row: any) => {
      if (row.profiles && !row.users) {
        row.users = row.profiles;
      }
      return row;
    }) as OfficialHistory[];
    return history;
  } catch (error) {
    console.error('Error getting official history:', error);
    throw error;
  }
}

/**
 * Get league-wide activity history (all officials in a league)
 * Fetches history for all officials that belong to the specified league
 */
export async function getLeagueActivityHistory(
  leagueId: string,
  limit: number = 100
): Promise<OfficialHistory[]> {
  try {
    // First, get all official IDs that belong to this league
    const { data: leagueOfficials, error: leagueError } = await supabase
      .from('league_officials')
      .select('official_id')
      .eq('league_id', leagueId);

    if (leagueError) {
      logAndThrow(leagueError, 'Failed to get league officials');
    }

    const officialIds = (leagueOfficials || []).map((lo) => lo.official_id);

    if (officialIds.length === 0) {
      return [];
    }

    // Then fetch history for all these officials
    const { data, error } = await supabase
      .from('official_history')
      .select(`
        *,
        officials!inner (
          id,
          first_name,
          last_name,
          first_name_ar,
          last_name_ar
        ),
        profiles!changed_by_user_id (
          id,
          email,
          full_name
        ),
        leagues!changed_by_league_id (
          id,
          name
        )
      `)
      .in('official_id', officialIds)
      .order('changed_at', { ascending: false })
      .limit(limit);

    if (error) {
      logAndThrow(error, 'Failed to get league activity history');
    }

    // Alias profiles to users for backward compatibility
    const history = (data || []).map((row: any) => {
      if (row.profiles && !row.users) {
        row.users = row.profiles;
      }
      if (row.officials) {
        row.official = row.officials;
      }
      return row;
    }) as OfficialHistory[];
    return history;
  } catch (error) {
    console.error('Error getting league activity history:', error);
    throw error;
  }
}

/**
 * Get upcoming assignments and unavailabilities for an official.
 * Designed for lightweight planning previews in the detail drawer.
 */
export async function getOfficialUpcomingPlanning(
  officialId: string,
  options: OfficialPlanningOptions = {}
): Promise<OfficialPlanningSnapshot> {
  if (!officialId) {
    return { assignments: [], unavailabilities: [] };
  }

  const { assignmentsLimit = 6, unavailabilitiesLimit = 4, leagueId } = options;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = today.toISOString().slice(0, 10);

  type AssignmentRow = {
    id: string;
    match_id: string;
    role: string;
    is_confirmed: boolean | null;
    matches: {
      id: string;
      match_date: string | null;
      match_time: string | null;
      status: string;
      league_group_id: string;
      league_group: {
        id: string;
        name: string;
        league_id: string | null;
        league: { id: string; name: string } | null;
      } | null;
      stadium: { id: string; name: string | null } | null;
      home_team: { id: string; name: string; name_ar?: string | null; logo_url?: string | null } | null;
      away_team: { id: string; name: string; name_ar?: string | null; logo_url?: string | null } | null;
    } | null;
  };

  const assignmentsQuery = supabase
    .from('match_assignments')
    .select(`
      id,
      match_id,
      role,
      is_confirmed,
      matches:matches!inner(
        id,
        match_date,
        match_time,
        status,
        league_group_id,
        league_group:league_groups!inner(
          id,
          name,
          league_id,
          league:leagues!league_groups_league_id_fkey(id,name)
        ),
        stadium:stadiums(id,name),
        home_team:teams!matches_home_team_id_fkey(id,name,name_ar,logo_url),
        away_team:teams!matches_away_team_id_fkey(id,name,name_ar,logo_url)
      )
    `)
    .eq('official_id', officialId)
    .gte('matches.match_date', minDate)
    // NOTE: Embedded ordering on joined table columns (matches.match_date, matches.match_time)
    // was causing PGRST100 parse errors on some PostgREST versions / cached schemas.
    // We remove server-side ordering and perform deterministic client-side sorting instead.
    .limit(leagueId ? assignmentsLimit * 3 : assignmentsLimit);

  const unavailabilitiesQuery = supabase
    .from('official_unavailabilities')
    .select('id, start_date, end_date, reason, is_approved')
    .eq('official_id', officialId)
    .gte('end_date', minDate)
    .order('start_date', { ascending: true })
    .limit(unavailabilitiesLimit);

  const [assignmentsResult, unavailabilitiesResult] = await Promise.all([
    assignmentsQuery,
    unavailabilitiesQuery,
  ]);

  if (assignmentsResult.error) {
    logAndThrow(assignmentsResult.error, 'Failed to fetch upcoming assignments', { officialId });
  }

  if (unavailabilitiesResult.error) {
    logAndThrow(unavailabilitiesResult.error, 'Failed to fetch upcoming unavailabilities', { officialId });
  }

  const assignmentsRows = ((assignmentsResult.data ?? []) as unknown) as AssignmentRow[];

  const assignments: OfficialUpcomingAssignment[] = assignmentsRows
    .map((row) => {
      const match = row.matches;
      const leagueGroup = match?.league_group ?? null;
      const league = leagueGroup?.league ?? null;

      const mapped: OfficialUpcomingAssignment = {
        id: row.id,
        matchId: row.match_id,
        role: row.role,
        matchDate: match?.match_date ?? null,
        matchTime: match?.match_time ?? null,
        isConfirmed: !!row.is_confirmed,
        leagueId: leagueGroup?.league_id ?? league?.id ?? undefined,
        leagueName: league?.name ?? undefined,
        leagueGroupId: leagueGroup?.id ?? undefined,
        leagueGroupName: leagueGroup?.name ?? undefined,
        stadiumId: match?.stadium?.id ?? null,
        stadiumName: match?.stadium?.name ?? null,
        homeTeam: match?.home_team
          ? {
            id: match.home_team.id,
            name: match.home_team.name,
            nameAr: match.home_team.name_ar ?? null,
            logoUrl: match.home_team.logo_url ?? null,
          }
          : undefined,
        awayTeam: match?.away_team
          ? {
            id: match.away_team.id,
            name: match.away_team.name,
            nameAr: match.away_team.name_ar ?? null,
            logoUrl: match.away_team.logo_url ?? null,
          }
          : undefined,
      };

      return mapped;
    })
    .filter((assignment) => !leagueId || assignment.leagueId === leagueId)
    .slice(0, assignmentsLimit);

  const unavailabilities: OfficialUpcomingUnavailability[] = (unavailabilitiesResult.data || []).map(
    (row: any) => ({
      id: row.id,
      startDate: row.start_date,
      endDate: row.end_date,
      reason: row.reason ?? null,
      isApproved: !!row.is_approved,
    })
  );

  return { assignments, unavailabilities };
}

/**
 * Build a synthetic recent activity timeline combining past assignments and unavailabilities.
 * Fallback when audit-driven official_history is empty or not yet populated.
 */
export async function getOfficialSyntheticActivity(
  officialId: string,
  options: OfficialActivityOptions = {}
): Promise<OfficialActivityEntry[]> {
  if (!officialId) return [];
  const limit = options.limit ?? 10;
  const lookbackDays = options.lookbackDays ?? 60;
  const today = new Date();
  const cutoff = new Date(today.getTime() - lookbackDays * 24 * 60 * 60 * 1000);
  const cutoffDate = cutoff.toISOString().slice(0, 10);

  // Fetch assignments within lookback window (match_date >= cutoff, <= today)
  const assignmentsBase = supabase
    .from('match_assignments')
    .select(`
      id,
      role,
      match_id,
      matches:matches!inner(
        id,
        match_date,
        home_team:teams!matches_home_team_id_fkey(id,name),
        away_team:teams!matches_away_team_id_fkey(id,name)
      )
    `)
    .eq('official_id', officialId)
    .gte('matches.match_date', cutoffDate)
    .lte('matches.match_date', today.toISOString().slice(0, 10))
    .limit(limit * 2); // fetch extra for merging

  const unavsBase = supabase
    .from('official_unavailabilities')
    .select('id,start_date,end_date,reason,is_approved')
    .eq('official_id', officialId)
    .gte('start_date', cutoffDate)
    .limit(limit * 2);

  const [assignmentsResult, unavsResult] = await Promise.all([
    assignmentsBase,
    unavsBase,
  ]);

  if (assignmentsResult.error) {
    console.warn('Synthetic activity assignments fetch error', assignmentsResult.error);
  }
  if (unavsResult.error) {
    console.warn('Synthetic activity unavailabilities fetch error', unavsResult.error);
  }

  const assignmentEntries: OfficialActivityEntry[] = (assignmentsResult.data || []).map((row: any) => {
    const match = row.matches;
    const matchDate = match?.match_date ?? null;
    return {
      id: `assign-${row.id}`,
      date: matchDate ? new Date(matchDate).toISOString() : new Date().toISOString(),
      type: 'assignment',
      title: 'Désignation',
      description: match && match.home_team && match.away_team
        ? `${match.home_team.name} vs ${match.away_team.name}`
        : 'Match (données partielles)',
      details: row.role,
      meta: { matchId: match?.id, role: row.role },
    } satisfies OfficialActivityEntry;
  });

  const unavailabilityEntries: OfficialActivityEntry[] = (unavsResult.data || []).map((row: any) => {
    return {
      id: `unav-${row.id}`,
      date: new Date(row.start_date).toISOString(),
      type: 'unavailability',
      title: "Indisponibilité",
      description: row.reason || "Période d'indisponibilité",
      details: `Du ${row.start_date} au ${row.end_date}`,
      meta: { startDate: row.start_date, endDate: row.end_date, reason: row.reason },
    } satisfies OfficialActivityEntry;
  });

  return [...assignmentEntries, ...unavailabilityEntries]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
}

/**
 * Get past assignments for an official (designation history).
 * Returns most recent matches (by match_date desc) up to the provided limit within an optional lookback window.
 */
export async function getOfficialAssignmentHistory(
  officialId: string,
  options?: { limit?: number; lookbackDays?: number; leagueId?: string | null }
) {
  if (!officialId) return [];
  const limit = options?.limit ?? 10;
  const lookbackDays = options?.lookbackDays ?? 120; // allow larger window for history
  const today = new Date();
  const cutoff = new Date(today.getTime() - lookbackDays * 24 * 60 * 60 * 1000);
  const cutoffDate = cutoff.toISOString().slice(0, 10);

  type Row = {
    id: string;
    role: string;
    match_id: string;
    is_confirmed: boolean | null;
    matches: {
      id: string;
      match_date: string | null;
      match_time: string | null;
      status: string | null;
      league_group_id: string | null;
      league_group: { id: string; name: string; league_id: string | null; league: { id: string; name: string } | null } | null;
      stadium: { id: string; name: string | null } | null;
      home_team: { id: string; name: string } | null;
      away_team: { id: string; name: string } | null;
    } | null;
  };

  // Fetch past assignments (match_date <= today) within window
  const { data, error } = await supabase
    .from('match_assignments')
    .select(`
      id,
      match_id,
      role,
      is_confirmed,
      matches:matches!inner(
        id,
        match_date,
        match_time,
        status,
        league_group_id,
        league_group:league_groups!inner(
          id,
          name,
          league_id,
          league:leagues!league_groups_league_id_fkey(id,name)
        ),
        stadium:stadiums(id,name),
        home_team:teams!matches_home_team_id_fkey(id,name),
        away_team:teams!matches_away_team_id_fkey(id,name)
      )
    `)
    .eq('official_id', officialId)
    .gte('matches.match_date', cutoffDate)
    .lte('matches.match_date', today.toISOString().slice(0, 10))
    .limit(limit * 3); // fetch extra for client filtering & sorting

  if (error) {
    console.warn('Failed to fetch assignment history', error);
    return [];
  }

  // Supabase join shape sometimes wraps single objects in arrays during hydration mismatch; coerce cautiously
  const rawRows = (data || []) as unknown as any[];
  const rows: Row[] = rawRows.map(r => ({
    id: r.id,
    role: r.role,
    match_id: r.match_id,
    is_confirmed: r.is_confirmed,
    matches: r.matches && !Array.isArray(r.matches) ? r.matches : (Array.isArray(r.matches) ? r.matches[0] : null),
  }));
  const leagueFilter = options?.leagueId ?? null;

  const mapped = rows
    .map(r => {
      const match = r.matches;
      const leagueGroup = match?.league_group ?? null;
      const league = leagueGroup?.league ?? null;
      return {
        id: r.id,
        matchId: r.match_id,
        role: r.role,
        matchDate: match?.match_date ?? null,
        matchTime: match?.match_time ?? null,
        isConfirmed: !!r.is_confirmed,
        leagueId: leagueGroup?.league_id ?? league?.id ?? undefined,
        leagueName: league?.name ?? undefined,
        leagueGroupId: leagueGroup?.id ?? undefined,
        leagueGroupName: leagueGroup?.name ?? undefined,
        stadiumId: match?.stadium?.id ?? null,
        stadiumName: match?.stadium?.name ?? null,
        homeTeam: match?.home_team ? { id: match.home_team.id, name: match.home_team.name } : undefined,
        awayTeam: match?.away_team ? { id: match.away_team.id, name: match.away_team.name } : undefined,
      };
    })
    .filter(a => a.matchDate !== null);

  const filteredByLeague = leagueFilter
    ? mapped.filter(a => a.leagueId === leagueFilter)
    : mapped;

  const sorted = filteredByLeague.sort((a, b) => {
    const ad = new Date(a.matchDate!).getTime();
    const bd = new Date(b.matchDate!).getTime();
    return bd - ad;
  });

  return sorted.slice(0, limit);
}

// ============= Helper Functions =============

/**
 * Simple name similarity calculation (Levenshtein distance based)
 */
function calculateNameSimilarity(name1: string, name2: string): number {
  // Normalize: Lowercase AND collapse multiple spaces to single space
  const s1 = (name1 || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const s2 = (name2 || '').toLowerCase().replace(/\s+/g, ' ').trim();

  if (!s1 || !s2) return 0;
  if (s1 === s2) return 1;

  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;

  if (longer.length === 0) return 0;

  const editDistance = getEditDistance(shorter, longer);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate Levenshtein edit distance
 */
function getEditDistance(s1: string, s2: string): number {
  const costs: number[] = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) {
      costs[s2.length] = lastValue;
    }
  }
  return costs[s2.length];
}

// ============= Lightweight Stats Functions =============

/**
 * Stats counts returned by the RPC
 */
export interface LeagueOfficialsCountsResult {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  pending: number;
  multiLeague: number;
  missingEmail: number;
  missingPhone: number;
  missingRib: number;
  invalidRib: number;
  incompleteProfile: number;
}

/**
 * Get aggregated counts for league officials without fetching all rows
 * Uses server-side RPC for ~90% less payload than fetching all officials
 */
export async function getLeagueOfficialsCounts(
  leagueId: string
): Promise<LeagueOfficialsCountsResult> {
  try {
    const { data, error } = await supabase.rpc('get_league_officials_counts', {
      p_league_id: leagueId,
    });

    if (error) {
      logAndThrow(error, 'Failed to get league officials counts');
    }

    // RPC returns JSONB, parse it
    const counts = data as Record<string, number> | null;

    return {
      total: counts?.total ?? 0,
      active: counts?.active ?? 0,
      inactive: counts?.inactive ?? 0,
      suspended: counts?.suspended ?? 0,
      pending: counts?.pending ?? 0,
      multiLeague: counts?.multiLeague ?? 0,
      missingEmail: counts?.missingEmail ?? 0,
      missingPhone: counts?.missingPhone ?? 0,
      missingRib: counts?.missingRib ?? 0,
      invalidRib: counts?.invalidRib ?? 0,
      incompleteProfile: counts?.incompleteProfile ?? 0,
    };
  } catch (error) {
    console.error('Error getting league officials counts:', error);
    throw error;
  }
}